import { useReactiveVar } from "@apollo/client";
import { withErrorBoundary } from "@sentry/react";
import { compact } from "lodash";
import moment from "moment";
import { useEffect, useMemo, useState } from "react";
import { MeetingNode } from "types/graphql-schema";
import { BasicUser } from "types/topicflow";

import Feedbacks from "@apps/dashboard-new/components/feedbacks";
import Recognitions from "@apps/dashboard-new/components/recognitions";
import EmployeeDrawerReviews from "@apps/reporting/components/assessment-report/employee-drawer/employee-drawer-reviews";
import useLabel from "@apps/use-label/use-label";
import useUiPreferenceCache, {
  UiPreferenceCache,
} from "@apps/use-ui-preference-cache/use-ui-preference-cache";
import WorkHistorySidebar from "@apps/work-history/components/work-history-sidebar";
import {
  currentOrganizationVar,
  currentUserVar,
  isAdminVar,
} from "@cache/cache";
import ErrorFallback from "@components/error-fallback/error-fallback";
import Layout from "@components/layout/layout";
import { ToggleButtonGroupType } from "@components/toggle-button-group/toggle-button-group";

import AssessmentAskAI from "./assessment-ask-ai";
import AssessmentGoalSidebar from "./assessment/assessment-goal-sidebar";
import RelatedRequirements from "./assessment/related-requirements";
import AssessmentRoleSidebar from "./assessment/role-sidebar";
import { AssessmentSidebarTab } from "./assessment/types";
import AssessmentContextSidebar from "./context-sidebar/context-sidebar";
import { assessmentContextSidebarVar } from "./context-sidebar/context-sidebar-context";

const AssessmentLayoutSidebarContainer = ({
  target,
  complianceProgramId,
  periodStartDate,
  periodEndDate,
  relatedMeetings,
  isManager,
  sidebarExpandedUiPreferenceKey,
  assessmentId,
}: {
  target: BasicUser;
  complianceProgramId: number;
  periodStartDate?: string | null;
  periodEndDate?: string | null;
  relatedMeetings: Pick<
    MeetingNode,
    | "id"
    | "title"
    | "meetingGroupId"
    | "startDatetime"
    | "endDatetime"
    | "draft"
  >[];
  isManager: boolean;
  sidebarExpandedUiPreferenceKey: keyof UiPreferenceCache;
  assessmentId?: number;
}) => {
  const label = useLabel();
  const isAdmin = isAdminVar();
  const organization = currentOrganizationVar();
  const currentUser = currentUserVar();
  const [chatSessionId, setChatSessionId] = useState<null | number>(null);
  const [startedChatSession, setStartedChatSession] = useState(true);
  const { uiPreferenceCache, saveUiPreference } = useUiPreferenceCache();

  const isInAssessment = !!assessmentId;

  const hasNoContentInArtifactTab =
    (!organization.featureFlags.meetings ||
      (!organization.featureFlags.meetings && relatedMeetings.length === 0)) &&
    !organization.featureFlags.goals &&
    !organization.featureFlags.recognitions &&
    !organization.featureFlags.feedbacks;

  const tabs = useMemo(() => {
    return compact([
      (!isInAssessment ||
        isAdmin ||
        isManager ||
        currentUser.id === target.id) && {
        title: label("review", { capitalize: true, pluralize: true }),
        value: AssessmentSidebarTab.reviews,
      },
      !hasNoContentInArtifactTab && {
        title: AssessmentSidebarTab.artifacts,
        value: AssessmentSidebarTab.artifacts,
      },
      organization.featureFlags.workHistory && {
        title: AssessmentSidebarTab.workHistory,
        value: AssessmentSidebarTab.workHistory,
      },
      organization.featureFlags.askAi && {
        title: AssessmentSidebarTab.askAI,
        value: AssessmentSidebarTab.askAI,
      },
      {
        title: AssessmentSidebarTab.roles,
        value: AssessmentSidebarTab.roles,
      },
      organization.featureFlags.reviewContextGeneration &&
        waffle.flag_is_active("review-context-sidebar") && {
          title: AssessmentSidebarTab.context,
          value: AssessmentSidebarTab.context,
        },
    ]);
  }, [
    isInAssessment,
    currentUser.id,
    target.id,
    isAdmin,
    isManager,
    label,
    organization.featureFlags,
    hasNoContentInArtifactTab,
  ]);

  // Perist last selected tab
  // or, if items tab is empty, show roles and reviews tab
  const defaultTabValue =
    uiPreferenceCache.assessmentSidebarSelectedTab ||
    (hasNoContentInArtifactTab && target
      ? AssessmentSidebarTab.roles
      : AssessmentSidebarTab.artifacts);
  const selectedActiveTab =
    tabs.find((tab) => tab.value === defaultTabValue) || tabs[0];

  const [activeTab, setActiveTab] = useState(selectedActiveTab.value);

  const handleChangeTab = (
    tab: ToggleButtonGroupType<AssessmentSidebarTab>,
  ) => {
    if (!tab.value) {
      return;
    }
    if (tab.value !== AssessmentSidebarTab.askAI) {
      assessmentContextSidebarVar(null);
    }
    saveUiPreference({
      assessmentSidebarSelectedTab: tab.value,
    });
  };

  const assessmentContextSidebar = useReactiveVar(assessmentContextSidebarVar);
  useEffect(() => {
    if (
      assessmentContextSidebar &&
      organization.featureFlags.reviewContextGeneration
    ) {
      const hasContextTab = tabs.some(
        (tab) => tab.value === AssessmentSidebarTab.context,
      );
      const hasAskAITab = tabs.some(
        (tab) => tab.value === AssessmentSidebarTab.askAI,
      );
      if (
        hasContextTab &&
        organization.featureFlags.reviewContextGeneration &&
        waffle.flag_is_active("review-context-sidebar") &&
        uiPreferenceCache.assessmentSidebarSelectedTab !==
          AssessmentSidebarTab.context
      ) {
        saveUiPreference({
          assessmentSidebarSelectedTab: AssessmentSidebarTab.context,
        });
      } else if (
        waffle.flag_is_active("generated-context-in-ask-ai") &&
        hasAskAITab &&
        uiPreferenceCache.assessmentSidebarSelectedTab !==
          AssessmentSidebarTab.askAI
      ) {
        saveUiPreference({
          assessmentSidebarSelectedTab: AssessmentSidebarTab.askAI,
        });
      }
    }
  }, [assessmentContextSidebar]);

  useEffect(() => {
    if (
      uiPreferenceCache.assessmentSidebarSelectedTab &&
      tabs.find(
        (tab) => tab.value === uiPreferenceCache.assessmentSidebarSelectedTab,
      )
    ) {
      setActiveTab(uiPreferenceCache.assessmentSidebarSelectedTab);
    }
  }, [tabs, uiPreferenceCache.assessmentSidebarSelectedTab]);

  return (
    <Layout.Sidebar<AssessmentSidebarTab>
      sidebarExpandedUiPreferenceKey={sidebarExpandedUiPreferenceKey}
      className="bg-white"
      onChangeTab={handleChangeTab}
      tabs={tabs.map((tab) => ({
        ...tab,
        active: tab.value === activeTab,
      }))}
      fullHeight={activeTab === AssessmentSidebarTab.askAI}
    >
      {activeTab === AssessmentSidebarTab.askAI ? (
        <AssessmentAskAI
          startedChatSession={startedChatSession}
          chatSessionId={chatSessionId}
          onChangeChatSessionId={setChatSessionId}
          assessmentId={assessmentId}
          onStartChatSession={() => {
            setStartedChatSession(true);
          }}
        />
      ) : activeTab === AssessmentSidebarTab.workHistory ? (
        <WorkHistorySidebar
          userId={target.id}
          startDate={
            periodStartDate || moment().subtract(7, "days").format("YYYY-MM-DD")
          }
          endDate={periodEndDate || moment().format("YYYY-MM-DD")}
          forceDateFilter={true}
          hasViewingPermission={isManager || currentUser.id === target.id}
        />
      ) : activeTab === AssessmentSidebarTab.artifacts ? (
        <>
          {hasNoContentInArtifactTab && (
            <Layout.SidebarSection>
              <Layout.SidebarSubSectionEmpty>
                No related data.
              </Layout.SidebarSubSectionEmpty>
            </Layout.SidebarSection>
          )}

          {organization.featureFlags.meetings && (
            <RelatedRequirements relatedMeetings={relatedMeetings} />
          )}

          {organization.featureFlags.goals && target && (
            <AssessmentGoalSidebar
              selectedUser={target}
              goalDueBetweenDates={
                periodStartDate && periodEndDate
                  ? [periodStartDate, periodEndDate]
                  : undefined
              }
            />
          )}

          {organization.featureFlags.recognitions && target && (
            <Recognitions
              selectedUser={target}
              createdBetweenDates={
                periodStartDate && periodEndDate
                  ? [periodStartDate, periodEndDate]
                  : undefined
              }
            />
          )}
          {organization.featureFlags.feedbacks && target && (
            <Feedbacks
              selectedUser={target}
              createdBetweenDates={
                periodStartDate && periodEndDate
                  ? [periodStartDate, periodEndDate]
                  : undefined
              }
            />
          )}
        </>
      ) : activeTab === AssessmentSidebarTab.roles && target ? (
        <AssessmentRoleSidebar target={target} />
      ) : activeTab === AssessmentSidebarTab.reviews && target ? (
        <EmployeeDrawerReviews
          target={target}
          preferredDeliveryProgramId={
            isInAssessment ? complianceProgramId : undefined
          }
          excludedProgramIds={
            !isInAssessment ? [complianceProgramId] : undefined
          }
        />
      ) : activeTab === AssessmentSidebarTab.context &&
        organization.featureFlags.reviewContextGeneration &&
        target ? (
        <AssessmentContextSidebar />
      ) : null}
    </Layout.Sidebar>
  );
};

const AssessmentLayoutSidebar = withErrorBoundary(
  AssessmentLayoutSidebarContainer,
  {
    fallback: ErrorFallback,
  },
);

export default AssessmentLayoutSidebar;
